import type { FC } from 'react'
import { useNavigate } from 'react-router'
import {
  Box,
  LinearProgress,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import { type Product, useMembersCareGaps } from './MembersDashboardService'
import { GREEN, RED } from '~/shared/lib/theme'
import { queuePath } from './queueRoutes'

const CARD_RADIUS = 2

const fmt = (n: number) => n.toLocaleString()
const pct = (rate: number | null | undefined) =>
  rate == null ? '—' : `${(rate * 100).toFixed(1)}%`

// Open care gaps per measure. Loads from its own endpoint so the slower per-measure
// rollup shows a loader on this card rather than blocking the overview tiles.
const CareGapsPanel: FC<{ product: Product; contract: number | null }> = ({
  product,
  contract,
}) => {
  const navigate = useNavigate()
  const { data, isPending: isLoading, error } = useMembersCareGaps(product, contract)
  const careGaps = data?.care_gaps ?? []
  const totalOpenGaps = careGaps.reduce((sum, gap) => sum + gap.open_gaps, 0)

  return (
    <Paper
      variant="outlined"
      sx={{ borderColor: 'grey.100', borderRadius: CARD_RADIUS, overflow: 'hidden' }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          px: 2,
          py: 1.5,
          borderBottom: '1px solid',
          borderColor: 'grey.50',
        }}
      >
        <Typography
          sx={{
            fontSize: 13,
            fontWeight: 700,
            letterSpacing: '0.04em',
            textTransform: 'uppercase',
            color: 'grey.600',
          }}
        >
          Open care gaps by measure
        </Typography>
        {!isLoading && error == null && (
          <Typography variant="body2" sx={{ color: 'grey.500' }}>
            {fmt(totalOpenGaps)} open · {careGaps.length} measure
            {careGaps.length === 1 ? '' : 's'}
          </Typography>
        )}
      </Box>

      {isLoading ? (
        <Box sx={{ p: 2 }}>
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton
              key={i}
              variant="rectangular"
              height={28}
              sx={{ my: 1.25, borderRadius: 1 }}
            />
          ))}
        </Box>
      ) : error != null ? (
        <Box sx={{ p: 2 }}>
          <Typography sx={{ color: 'error.main' }}>Failed to load care gaps.</Typography>
        </Box>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow
              sx={{
                '& th': {
                  bgcolor: 'grey.50',
                  borderColor: 'grey.100',
                  fontSize: 11,
                  fontWeight: 600,
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                  color: 'grey.500',
                  py: 1,
                },
              }}
            >
              <TableCell>Measure</TableCell>
              <TableCell align="right">Open gaps</TableCell>
              <TableCell align="right">Eligible</TableCell>
              <TableCell sx={{ width: '28%' }}>Closure rate</TableCell>
              <TableCell sx={{ width: 36 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {careGaps.map(gap => (
              <TableRow
                key={gap.id}
                hover
                onClick={() => void navigate(queuePath(`gap_${gap.id}`, product, contract))}
                sx={{
                  cursor: 'pointer',
                  '& td': { borderColor: 'grey.50' },
                  '&:last-of-type td': { borderBottom: 'none' },
                }}
              >
                <TableCell>
                  <Typography variant="body2" noWrap sx={{ color: 'text.primary' }}>
                    {gap.name ?? gap.measure_config}
                  </Typography>
                </TableCell>
                <TableCell
                  align="right"
                  sx={{
                    color: gap.open_gaps > 0 ? RED : 'text.primary',
                    fontWeight: 500,
                    fontSize: 15,
                  }}
                >
                  {fmt(gap.open_gaps)}
                </TableCell>
                <TableCell align="right" sx={{ color: 'grey.500' }}>
                  {fmt(gap.eligible)}
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LinearProgress
                      variant="determinate"
                      value={(gap.closure_rate ?? 0) * 100}
                      sx={{
                        flex: 1,
                        height: 8,
                        borderRadius: 1,
                        bgcolor: 'grey.50',
                        '& .MuiLinearProgress-bar': { bgcolor: GREEN },
                      }}
                    />
                    <Typography variant="body2" sx={{ width: 48, textAlign: 'right' }}>
                      {pct(gap.closure_rate)}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell align="right" sx={{ pr: 1.5 }}>
                  <ChevronRightIcon sx={{ color: 'grey.200', fontSize: 20, display: 'block' }} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Paper>
  )
}

export default CareGapsPanel
